/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios';
import { put, call, takeLatest, select } from 'redux-saga/effects';
import {
  types,
  Event,
  ClientIntegration,
  GetTicketingEventsRequest,
  UpdateClientIntegrationRequest,
  CreateClientIntegrationRequest,
  DeleteClientIntegrationRequest,
  CreateCustomerRequest,
  CreateCustomerResponse,
  TestAccessRequest,
  GetPaymentSettingsRequest,
  UpdatePaymentSettingsRequest,
  GetPaymentSettingsResponse
} from './types';
import { selectors as clientSelectors } from 'store/clients';
import { API } from 'services/api/base';
import { getErrorCode, getErrorMessage, getResponseErrorCode } from 'services/api/normalizer';
import { ADMIN_ERROR_MESSAGES } from 'constants/errors';

export function* getClientIntegrationsAsync() {
  yield put({ type: types.GET_CLIENT_INTEGRATIONS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/clientIntegrations`);
    const response: ClientIntegration[] = yield call(api.getAll);

    yield put({ type: types.GET_CLIENT_INTEGRATIONS.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_CLIENT_INTEGRATIONS.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_CLIENT_INTEGRATIONS.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.GET_CLIENT_INTEGRATIONS.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.GET_CLIENT_INTEGRATIONS.PENDING, payload: false });
  }
}

export function* createClientIntegrationAsync({ payload }: CreateClientIntegrationRequest) {
  yield put({ type: types.CREATE_CLIENT_INTEGRATION.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/clientIntegrations`);
    yield call(api.create, payload);

    yield put({ type: types.CREATE_CLIENT_INTEGRATION.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_CLIENT_INTEGRATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_CLIENT_INTEGRATION.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.CREATE_CLIENT_INTEGRATION.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.CREATE_CLIENT_INTEGRATION.PENDING, payload: false });
  }
}

export function* updateClientIntegrationAsync({ payload }: UpdateClientIntegrationRequest) {
  yield put({ type: types.UPDATE_CLIENT_INTEGRATION.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/clientIntegrations`);
    const response: ClientIntegration = yield call(api.update, payload);

    yield put({ type: types.UPDATE_CLIENT_INTEGRATION.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_CLIENT_INTEGRATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_CLIENT_INTEGRATION.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.UPDATE_CLIENT_INTEGRATION.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.UPDATE_CLIENT_INTEGRATION.PENDING, payload: false });
  }
}

export function* deleteClientIntegrationAsync({ payload }: DeleteClientIntegrationRequest) {
  const { id } = payload;
  yield put({ type: types.DELETE_CLIENT_INTEGRATION.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/clientIntegrations`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_CLIENT_INTEGRATION.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    const responseErrorCode = getResponseErrorCode(err as AxiosError);

    if (responseErrorCode && ADMIN_ERROR_MESSAGES[responseErrorCode]) {
      yield put({
        type: types.DELETE_CLIENT_INTEGRATION.ERROR,
        payload: { errorMessage: ADMIN_ERROR_MESSAGES[responseErrorCode], errorCode: responseErrorCode }
      });
    } else if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_CLIENT_INTEGRATION.ERROR, payload: { errorMessage: 'Invalid credentials' } });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_CLIENT_INTEGRATION.ERROR, payload: { errorMessage: 'Server error' } });
    } else {
      yield put({
        type: types.DELETE_CLIENT_INTEGRATION.ERROR,
        payload: { errorMessage: 'Uh oh! Something went wrong. Please try again.' }
      });
    }
  } finally {
    yield put({ type: types.DELETE_CLIENT_INTEGRATION.PENDING, payload: false });
  }
}

export function* getPaymentSettingsAsync({ payload }: GetPaymentSettingsRequest) {
  yield put({ type: types.GET_PAYMENT_SETTINGS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(`clients/${clientId}/clientIntegrations/${payload}/paymentSettings`);

    const response: GetPaymentSettingsResponse = yield call(api.getAll);

    yield put({
      type: types.GET_PAYMENT_SETTINGS.SUCCESS,
      payload: response
    });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({
        type: types.GET_PAYMENT_SETTINGS.ERROR,
        payload: 'Invalid credentials'
      });
    } else if (errorCode >= 500) {
      yield put({
        type: types.GET_PAYMENT_SETTINGS.ERROR,
        payload: 'Server error'
      });
    } else {
      yield put({
        type: types.GET_PAYMENT_SETTINGS.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.GET_PAYMENT_SETTINGS.PENDING, payload: false });
  }
}

export function* updatePaymentSettingsAsync({ payload }: UpdatePaymentSettingsRequest) {
  yield put({ type: types.UPDATE_PAYMENT_SETTINGS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const { id, data } = payload;

    const api = new API(`clients/${clientId}/clientIntegrations/${id}/paymentSettings`);
    yield call(api.putWithoutId, data);
    yield put({ type: types.UPDATE_PAYMENT_SETTINGS.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({
        type: types.UPDATE_PAYMENT_SETTINGS.ERROR,
        payload: 'Invalid credentials'
      });
    } else if (errorCode >= 500) {
      yield put({
        type: types.UPDATE_PAYMENT_SETTINGS.ERROR,
        payload: 'Server error'
      });
    } else {
      yield put({
        type: types.UPDATE_PAYMENT_SETTINGS.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.UPDATE_PAYMENT_SETTINGS.PENDING, payload: false });
  }
}

export function* getProductsAsync({ payload }: { payload: string }): any {
  yield put({ type: types.GET_PRODUCTS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/clientIntegrations/${payload}/products`);

    const response = yield call(api.getAll);

    yield put({
      type: types.GET_PRODUCTS.SUCCESS,
      payload: response
    });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_PRODUCTS.ERROR, payload: 'Invalid request' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_PRODUCTS.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.GET_PRODUCTS.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.GET_PRODUCTS.PENDING, payload: false });
  }
}

export function* syncProductsAsync({ payload }: { payload: any }): any {
  yield put({ type: types.SYNC_PRODUCTS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const { id, data } = payload;

    const api = new API(`clients/${clientId}/clientIntegrations/${id}/products`);

    const response = yield call(api.putWithoutId, data);

    yield put({
      type: types.SYNC_PRODUCTS.SUCCESS,
      payload: response
    });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.SYNC_PRODUCTS.ERROR, payload: 'Invalid request' });
    } else if (errorCode >= 500) {
      yield put({ type: types.SYNC_PRODUCTS.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.SYNC_PRODUCTS.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.SYNC_PRODUCTS.PENDING, payload: false });
  }
}

export function* getTicketingEventsAsync({ payload }: GetTicketingEventsRequest) {
  yield put({ type: types.GET_TICKETING_EVENTS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const clientsApi = new API(`clients/${clientId}/clientIntegrations/${payload}/events`);
    const response: { events: Event[] } = yield call(clientsApi.getAll);

    // add "Donation" so images can be uploaded if configured
    response.events.push({
      id: 'donation',
      name: 'Donation',
      genre: 'Donation',
      date: '',
      type: 'donation',
      venue: 'Donation',
      detailsParams: {}
    });

    yield put({ type: types.GET_TICKETING_EVENTS.SUCCESS, payload: response.events });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_TICKETING_EVENTS.ERROR, payload: 'No Events for the selected Enviornment' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_TICKETING_EVENTS.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_TICKETING_EVENTS.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_TICKETING_EVENTS.PENDING, payload: false });
  }
}

export function* createCustomerAsync({ payload }: CreateCustomerRequest) {
  yield put({ type: types.CREATE_CUSTOMER.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const { id, details } = payload;
    const api = new API(`clients/${clientId}/clientIntegrations/${id}/customers`);
    const response: CreateCustomerResponse = yield call(api.create, { details });

    yield put({ type: types.CREATE_CUSTOMER.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    const errorMsg = getErrorMessage(err as AxiosError, true) as string;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_CUSTOMER.ERROR, payload: errorMsg ? errorMsg : 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_CUSTOMER.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.CREATE_CUSTOMER.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.CREATE_CUSTOMER.PENDING, payload: false });
  }
}

export function* testAccessAsync({ payload: id }: TestAccessRequest) {
  yield put({ type: types.TEST_ACCESS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/clientIntegrations/${id}/testAccess`);
    yield call(api.getAll);

    yield put({ type: types.TEST_ACCESS.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    const errorMsg = getErrorMessage(err as AxiosError, true) as string;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({
        type: types.TEST_ACCESS.ERROR,
        payload: errorMsg ? `Access failed. Error details: ${errorMsg}` : 'Invalid credentials'
      });
    } else if (errorCode >= 500) {
      yield put({ type: types.TEST_ACCESS.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.TEST_ACCESS.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.TEST_ACCESS.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_CLIENT_INTEGRATIONS.REQUEST, getClientIntegrationsAsync),
  takeLatest(types.CREATE_CLIENT_INTEGRATION.REQUEST, createClientIntegrationAsync),
  takeLatest(types.UPDATE_CLIENT_INTEGRATION.REQUEST, updateClientIntegrationAsync),
  takeLatest(types.DELETE_CLIENT_INTEGRATION.REQUEST, deleteClientIntegrationAsync),
  takeLatest(types.GET_TICKETING_EVENTS.REQUEST, getTicketingEventsAsync),
  takeLatest(types.CREATE_CUSTOMER.REQUEST, createCustomerAsync),
  takeLatest(types.GET_PAYMENT_SETTINGS.REQUEST, getPaymentSettingsAsync),
  takeLatest(types.UPDATE_PAYMENT_SETTINGS.REQUEST, updatePaymentSettingsAsync),
  takeLatest(types.GET_PRODUCTS.REQUEST as any, getProductsAsync),
  takeLatest(types.SYNC_PRODUCTS.REQUEST as any, syncProductsAsync),
  takeLatest(types.TEST_ACCESS.REQUEST, testAccessAsync)
];
